import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { GRADERS, graderLabel, isSealedCategory, MARKET_CONDITIONS, MARKET_EXCLUDED_CATEGORIES, MARKET_LANGUAGES, MAX_PHOTOS, PENDING_LISTINGS, type MarketCondition, type MarketFinish, type PriceReference, type SellerPlan } from "../../shared/market";
import { formatArs } from "../../shared/money";
import { CONDITION_LABEL, FINISH_LABEL, LANGUAGE_LABEL } from "../../shared/variants";
import { gameFinishes } from "../../shared/games";
import { useI18n } from "../i18n/I18nProvider";
import { track } from "../lib/analytics";
import { useGame } from "../lib/GameProvider";
import { ApiError, imageUrl } from "../lib/api";
import { isPhotoType } from "../lib/resizeImage";
import { marketApi, type CatalogCard } from "./api";
import { CardPicker } from "./CardPicker";
import { LimitDialog } from "./Showcase";

/** What the form needs of the shop's plan. */
export type ShopPlan = { name: SellerPlan; liveListings: number; maxListings: number | null; maxPriceArs?: number | null };

type Step = 1 | 2 | 3 | "done";

/** What "Publicar otra" keeps for the next card (a binder is mostly the same condition and language). */
interface Kept {
  condition: MarketCondition;
  language: string;
  finish: MarketFinish;
}

/** The limit the server says was hit (listing_limit / price_limit carry it). */
const limitOf = (err: ApiError, fallback: number) => {
  const max = (err.details as { max?: unknown } | undefined)?.max;
  return typeof max === "number" ? max : fallback;
};

/**
 * Listing a card in three steps, one thing each: which card · how it is · how much (with what it
 * sells for, and its own photos if the seller wants them). "Publicar" creates it, uploads
 * the photos and puts it live in one go; "Publicar otra" starts over keeping condition and language.
 */
export function NewListing({
  pending,
  plan = null,
  initialCard = null,
  onCancel,
  onChanged,
  onEdit,
}: {
  pending: boolean;
  /** The shop's plan: its cap shows in step 3, and hitting it (or its price cap) opens the upgrade dialog. */
  plan?: ShopPlan | null;
  initialCard?: CatalogCard | null;
  onCancel: () => void;
  /** A listing was created (reload the list). */
  onChanged: () => void;
  /** Open that listing's editor (photos, price…). */
  onEdit: (id: number) => void;
}) {
  const { t, locale } = useI18n();
  const { game } = useGame();
  const [kept, setKept] = useState<Kept>({ condition: "NM", language: "es", finish: "normal" });
  // A box we added by hand in its own language (a Japanese one) is listed in it, for that listing only;
  // the imported catalog is all "en", and sellers there mostly have the Spanish print: their choice holds.
  const [boxLanguage, setBoxLanguage] = useState<string | null>(null);
  const language = boxLanguage ?? kept.language;
  const chooseLanguage = (l: string) => {
    setBoxLanguage(null);
    setKept({ ...kept, language: l });
  };
  const [card, setCard] = useState<CatalogCard | null>(initialCard);
  const sealedOf = (c: CatalogCard | null) => (c ? isSealedCategory(c.category) : false);
  const [step, setStep] = useState<Step>(initialCard ? (sealedOf(initialCard) ? 3 : 2) : 1);
  const [graded, setGraded] = useState(false);
  const [grader, setGrader] = useState<string>(GRADERS[0]);
  const [grade, setGrade] = useState("");
  const [notes, setNotes] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ id: number; live: boolean; photos: number } | null>(null);
  // Already created (and how many of its photos went up): trying again finishes it instead of creating another.
  const [created, setCreated] = useState<{ id: number; uploaded: number } | null>(null);
  const [limit, setLimit] = useState<"listing" | "price" | null>(null);
  const [startedAt, setStartedAt] = useState(() => Date.now());
  // "Igual que la anterior": the last one published here, to list the next card the same way (price and quantity too).
  const [last, setLast] = useState<{ sealed: boolean; price: number; quantity: number } | null>(null);
  const [repeat, setRepeat] = useState(false);
  const [repeated, setRepeated] = useState(false);
  const sealed = sealedOf(card);
  // The card's game decides its finishes (One Piece: plain only); a finish kept from another game's card falls back to plain.
  const finishes = gameFinishes(card?.game ?? game);
  const finish = !sealed && finishes.includes(kept.finish) ? kept.finish : "normal";
  const condition = sealed ? "SEALED" : kept.condition;
  const priceNum = Number(price);

  const pick = (c: CatalogCard) => {
    setCard(c);
    setCreated(null);
    setError(null);
    setBoxLanguage(sealedOf(c) && c.language && c.language !== "en" ? c.language : null);
    // Same kind as the last one (a card after a card): straight to the price, filled in, never sent by itself.
    if (repeat && last && last.sealed === sealedOf(c)) {
      setPrice(String(last.price));
      setQuantity(last.quantity);
      setRepeated(true);
      setStep(3);
      return;
    }
    setRepeated(false);
    setStep(sealedOf(c) ? 3 : 2);
  };

  // "Publicar otra": a new card, the same condition, language and finish.
  const again = () => {
    setCard(null);
    setRepeated(false);
    setGraded(false);
    setGrade("");
    setNotes("");
    setPrice("");
    setQuantity(1);
    setFiles([]);
    setError(null);
    setDone(null);
    setCreated(null);
    setStartedAt(Date.now());
    setStep(1);
  };

  /** Creates it (once), uploads its photos and, unless under review, puts it live. */
  const publish = async (e: FormEvent) => {
    e.preventDefault();
    if (!card) return;
    if (!(priceNum > 0)) return setError(t("nl.errPrice"));
    if (!sealed && graded && !grade) return setError(t("nl.errGrade"));
    setBusy(true);
    setError(null);
    const fields = {
      // Sealed products have no card condition, finish or grading.
      condition,
      language,
      finish,
      grader: !sealed && graded ? grader : null,
      grade: !sealed && graded ? Number(grade) : null,
      priceArs: priceNum,
      quantity,
      notes,
    };
    let id = created?.id ?? null;
    let uploaded = created?.uploaded ?? 0;
    const live = !pending;
    try {
      if (id === null) {
        ({ id } = await marketApi.createListing({ productId: card.id, ...fields }));
        setCreated({ id, uploaded: 0 });
        onChanged();
      }
      for (const f of files.slice(uploaded)) {
        await marketApi.uploadPhoto(id, f);
        uploaded += 1;
        setCreated({ id, uploaded });
      }
      // A second try sends what may have changed since (the price that was over the plan's cap…).
      if (live) await marketApi.updateListing(id, created ? { ...fields, status: "active" } : { status: "active" });
      track("seller_publish_done", { flow: "steps", seconds: Math.round((Date.now() - startedAt) / 1000), sealed: sealed ? "yes" : "no", repeated: repeated ? "yes" : "no" });
      setLast({ sealed, price: priceNum, quantity });
      setDone({ id, live, photos: uploaded });
      setStep("done");
      onChanged();
    } catch (err) {
      if (!pending && plan && err instanceof ApiError && (err.code === "listing_limit" || err.code === "price_limit")) setLimit(err.code === "listing_limit" ? "listing" : "price");
      // Created but not live (the plan's limit, its price cap, a photo that didn't go up…): it's saved, paused, and the seller is told why.
      setError(
        err instanceof ApiError && err.code === "listing_limit"
          ? t(pending ? "mkt.pendingLimit" : "mkt.listingLimit", { max: limitOf(err, PENDING_LISTINGS) })
          : err instanceof ApiError && err.code === "price_limit"
            ? t("mkt.priceLimit", { max: formatArs(limitOf(err, 0)) })
            : id !== null
                ? t("nl.savedPaused")
                : t("mkt.listingError"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="new-listing nl stack" aria-labelledby="nl-title">
      <h2 id="nl-title">{t("mkt.newListing")}</h2>
      {limit && plan && <LimitDialog kind={limit} plan={plan.name} live={plan.liveListings} price={priceNum} onClose={() => setLimit(null)} onPauseOther={onCancel} />}
      {step !== "done" && (
        <ol className="nl-steps" aria-label={t("nl.progress")}>
          {([1, 2, 3] as const).map((n) => (
            <li key={n} className={step === n ? "is-current" : typeof step === "number" && n < step ? "is-done" : undefined} aria-current={step === n ? "step" : undefined}>
              {t(`nl.step${n}` as never)}
            </li>
          ))}
        </ol>
      )}

      {card && step !== 1 && step !== "done" && (
        <div className="picked-card">
          <img src={imageUrl(card.imageKey)} alt="" width={60} height={84} />
          <div>
            <strong>{card.name}</strong>
            <div className="muted small">{[card.setName, card.cardNumber ? `#${card.cardNumber}` : null].filter(Boolean).join(" · ")}</div>
            <button type="button" className="btn btn-link btn-sm" onClick={() => setStep(1)}>
              {t("mkt.changeCard")}
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="stack">
          {last && (
            <label className="check nl-repeat">
              <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} />{" "}
              {t("nl.repeat", { what: [last.sealed ? t("nl.sealedShort") : CONDITION_LABEL[kept.condition]?.[locale] ?? kept.condition, LANGUAGE_LABEL[kept.language]?.[locale] ?? kept.language, `${formatArs(last.price)} × ${last.quantity}`].join(" · ") })}
            </label>
          )}
          <CardPicker onPick={pick} filters />
          <IdentifyPick onPick={pick} />
          <button type="button" className="btn btn-link nl-cancel" onClick={onCancel}>
            {t("cards.cancel")}
          </button>
        </div>
      )}

      {step === 2 && card && (
        <form className="stack" onSubmit={(e) => (e.preventDefault(), setStep(3))}>
          <fieldset className="nl-conditions">
            <legend>{t("mkt.f.condition")}</legend>
            {MARKET_CONDITIONS.map((c) => (
              <label key={c} className={kept.condition === c ? "nl-cond is-on" : "nl-cond"}>
                <input type="radio" name="condition" value={c} checked={kept.condition === c} onChange={() => setKept({ ...kept, condition: c })} className="sr-only" />
                <strong>{c}</strong>
                <span className="small">{CONDITION_LABEL[c][locale]}</span>
              </label>
            ))}
          </fieldset>
          <LanguageField value={language} onChange={chooseLanguage} />
          <label className="check">
            <input type="checkbox" checked={graded} onChange={(e) => setGraded(e.target.checked)} /> {t("nl.graded")}
          </label>
          {graded && (
            <div className="field-row">
              <div className="field">
                <label htmlFor="nl-grader">{t("mkt.grader")}</label>
                <select id="nl-grader" value={grader} onChange={(e) => setGrader(e.target.value)}>
                  {GRADERS.map((g) => (
                    <option key={g} value={g}>
                      {graderLabel(g)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="nl-grade">{t("mkt.grade")}</label>
                <input id="nl-grade" type="number" min={1} max={10} step={0.5} value={grade} onChange={(e) => setGrade(e.target.value)} />
              </div>
            </div>
          )}
          <details className="nl-more">
            <summary>{t("nl.more")}</summary>
            <div className="stack">
              {finishes.length > 1 && (
                <div className="field">
                  <label htmlFor="nl-finish">{t("mkt.f.finish")}</label>
                  <select id="nl-finish" value={finish} onChange={(e) => setKept({ ...kept, finish: e.target.value as MarketFinish })}>
                    {finishes.map((f) => (
                      <option key={f} value={f}>
                        {FINISH_LABEL[f][locale]}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <NotesField value={notes} onChange={setNotes} />
            </div>
          </details>
          <div className="row-actions">
            <button type="submit" className="btn btn-primary">
              {t("nl.next")}
            </button>
            <button type="button" className="btn btn-link" onClick={() => setStep(1)}>
              {t("nl.back")}
            </button>
          </div>
        </form>
      )}

      {step === 3 && card && (
        <form className="stack" onSubmit={publish} noValidate>
          {sealed && (
            <>
              <p className="muted small">{t("mkt.sealedNote")}</p>
              <LanguageField value={language} onChange={chooseLanguage} />
            </>
          )}
          <div className="field-row">
            <div className="field">
              <label htmlFor="nl-price">{t("mkt.priceArs")}</label>
              <input id="nl-price" type="number" min={1} inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} autoFocus />
              <PriceRef productId={card.id} condition={condition} onUse={(n) => setPrice(String(n))} />
              {repeated && <p className="small nl-repeat-hint">{t("nl.repeatHint")}</p>}
            </div>
            <div className="field">
              <span className="label" id="nl-qty-label">
                {t("mkt.quantity")}
              </span>
              <div className="stepper" role="group" aria-labelledby="nl-qty-label">
                <button type="button" aria-label={t("nl.less")} disabled={quantity <= 1} onClick={() => setQuantity(quantity - 1)}>
                  −
                </button>
                <output aria-live="polite">{quantity}</output>
                <button type="button" aria-label={t("nl.more1")} disabled={quantity >= 999} onClick={() => setQuantity(quantity + 1)}>
                  +
                </button>
              </div>
            </div>
          </div>
          {sealed && <NotesField value={notes} onChange={setNotes} />}
          {/* BASIC and EX: how much room is left, no button (the dialog comes if it doesn't fit). */}
          {!pending && plan && plan.maxListings !== null && (
            <p className="muted small nl-quota">
              {plan.maxListings - plan.liveListings <= 2 && plan.maxListings > plan.liveListings
                ? t("nl.quotaLow", { count: plan.maxListings - plan.liveListings })
                : t("nl.quota", { live: plan.liveListings, max: plan.maxListings })}
            </p>
          )}
          <PhotoPick files={files} onChange={setFiles} />
          <div className="row-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t("nl.publishing") : pending ? t("nl.save") : t("nl.publish")}
            </button>
            <button type="button" className="btn btn-link" onClick={() => setStep(sealed ? 1 : 2)}>
              {t("nl.back")}
            </button>
          </div>
          {error && (
            <p className="form-error" role="alert">
              {error}
              {created && (
                <>
                  {" "}
                  <button type="button" className="btn btn-link btn-sm" onClick={() => onEdit(created.id)}>
                    {t("nl.openSaved")}
                  </button>
                </>
              )}
            </p>
          )}
        </form>
      )}

      {step === "done" && done && (
        <div className="nl-done stack" role="status">
          <p className="nl-done-title">✓ {done.live ? t("nl.doneLive") : t("nl.donePending")}</p>
          <div className="btn-row">
            <button type="button" className="btn btn-primary" onClick={again}>
              {t("nl.another")}
            </button>
            {(done.photos === 0 || !done.live) && (
              <button type="button" className="btn btn-outline" onClick={() => onEdit(done.id)}>
                {t("nl.addPhotos")}
              </button>
            )}
            {done.live && (
              <Link to={`/mercado/${done.id}`} className="btn btn-outline">
                {t("nl.see")}
              </Link>
            )}
            <button type="button" className="btn btn-link" onClick={onCancel}>
              {t("nl.close")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function LanguageField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t, locale } = useI18n();
  return (
    <div className="field">
      <label htmlFor="nl-lang">{t("mkt.f.language")}</label>
      <select id="nl-lang" value={value} onChange={(e) => onChange(e.target.value)}>
        {MARKET_LANGUAGES.map((l) => (
          <option key={l} value={l}>
            {LANGUAGE_LABEL[l]?.[locale] ?? l}
          </option>
        ))}
      </select>
    </div>
  );
}

function NotesField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useI18n();
  return (
    <div className="field">
      <label htmlFor="nl-notes">{t("mkt.listingNotes")}</label>
      <textarea id="nl-notes" rows={2} maxLength={1000} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/** "Mediana en pokefans $X · mínimo $Y · N vendedores" (tap to use the median), or the last sale. Nothing is made up. */
function PriceRef({ productId, condition, onUse }: { productId: number; condition: string; onUse: (n: number) => void }) {
  const { t } = useI18n();
  const [ref, setRef] = useState<PriceReference | null | undefined>(undefined);
  useEffect(() => {
    const ctl = new AbortController();
    setRef(undefined);
    marketApi.priceRef(productId, condition, ctl.signal).then(setRef, () => setRef(null));
    return () => ctl.abort();
  }, [productId, condition]);
  if (!ref) return ref === null ? <p className="muted small nl-ref">{t("nl.refNone")}</p> : null;
  if (ref.source === "sale") return <p className="muted small nl-ref">{t("nl.refSale", { price: formatArs(ref.lastSaleArs) })}</p>;
  return (
    <p className="muted small nl-ref">
      <button type="button" className="btn btn-link btn-sm" onClick={() => onUse(ref.medianArs)}>
        {t("nl.refMedian", { price: formatArs(ref.medianArs) })}
      </button>{" "}
      · {t("nl.refMin", { price: formatArs(ref.minArs) })} · {t("nl.refSellers", { count: ref.sellers })}
    </p>
  );
}

/** The listing's own photos, picked before publishing; optional: without them it shows the catalog image. */
function PhotoPick({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const { t } = useI18n();
  const urls = useRef(new Map<File, string>());
  const urlOf = (f: File) => {
    if (!urls.current.has(f)) urls.current.set(f, URL.createObjectURL(f));
    return urls.current.get(f)!;
  };
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), []);
  // Any size: it's brought down before it goes up (resizeImage).
  const ok = isPhotoType;
  return (
    <div className="field">
      <span className="label">{t("nl.photosOptional")}</span>
      <div className="photo-strip">
        {files.map((f, i) => (
          <figure key={urlOf(f)}>
            <img src={urlOf(f)} alt={t("mkt.photoN", { n: i + 1 })} width={96} height={96} />
            <figcaption>
              <button type="button" className="btn btn-link btn-sm" onClick={() => onChange(files.filter((x) => x !== f))}>
                {t("mkt.deletePhoto")}
              </button>
            </figcaption>
          </figure>
        ))}
        {files.length < MAX_PHOTOS && (
          <label className="photo-add">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              multiple
              className="sr-only"
              onChange={(e) => {
                const picked = [...(e.target.files ?? [])].filter(ok);
                e.target.value = "";
                onChange([...files, ...picked].slice(0, MAX_PHOTOS));
              }}
            />
            <span>＋ {t("mkt.addPhoto")}</span>
          </label>
        )}
      </div>
    </div>
  );
}

/** "Identificar con una foto": the phone's camera, the card read from it, and the matches to pick from. */
function IdentifyPick({ onPick }: { onPick: (c: CatalogCard) => void }) {
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<CatalogCard[] | null>(null);
  const read = async (file: File) => {
    setBusy(true);
    setError(null);
    setFound(null);
    try {
      const { results } = await marketApi.identifyCard(file);
      const cards = results
        .filter((r) => !MARKET_EXCLUDED_CATEGORIES.includes(r.category))
        .map((r) => ({ id: r.id, name: locale === "en" ? r.name.en : r.name.es, setName: r.setName, cardNumber: r.cardNumber, rarity: r.rarity, imageKey: r.imageKey, category: r.category, game: r.game }));
      if (cards.length === 1) return onPick(cards[0]);
      setFound(cards);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      setError(t(["ocr_failed", "unsupported_language", "unsupported_image", "image_too_large", "rate_limited"].includes(code) ? (`nl.id.${code}` as never) : "nl.id.error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="nl-identify stack-sm">
      <label className="btn btn-outline nl-identify-btn">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="sr-only"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) void read(file);
          }}
        />
        📷 {busy ? t("nl.id.reading") : t("nl.id.button")}
      </label>
      {error && <p className="form-error">{error}</p>}
      {found && found.length === 0 && <p className="muted small">{t("nl.id.none")}</p>}
      {found && found.length > 1 && (
        <div className="stack-sm">
          <p className="small">{t("nl.id.pick")}</p>
          <ul className="nl-identify-list">
            {found.map((c) => (
              <li key={c.id}>
                <button type="button" className="nl-identify-card" onClick={() => onPick(c)}>
                  <img src={imageUrl(c.imageKey)} alt="" width={48} height={67} loading="lazy" />
                  <span>
                    <strong>{c.name}</strong>
                    <span className="muted small">{[c.setName, c.cardNumber ? `#${c.cardNumber}` : null].filter(Boolean).join(" · ")}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
